import PropTypes from 'prop-types'
import { Button, message, Switch, Table } from 'antd'
import { FileExcelOutlined, FileSearchOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo } from 'react'
import queryString from 'query-string'
import get from 'lodash/get'
import { useDispatch } from 'react-redux'
import { useTheme } from 'react-jss'
import { Link, useParams } from 'react-router-dom'
import { useIsAdmin } from '../../../redux/slices/session'
import { useAsync } from '../../../hooks'
import { PAGE_SIZE_OPTIONS } from '../../../lib/constants'
import {
  updateOfficeAuthorization,
  useFetchOfficeAuthorizations,
  useOfficeAuthorizations,
  useOfficeAuthorizationsPagination,
} from '../../../redux/slices/office-authorizations'
import { ListError } from '../../Common'
import { toPath } from '../../../routes'
import { formatDOB } from '../../../lib/datetime'
import useExportExcel from '../../../hooks/useExportExcel'
import DownloadLink from '../../Common/DownloadLink'
import CircleCheckIcon from '../../Common/CircleCheckIcon'

const BASE_COLUMNS = [
  {
    title: 'Patient',
    dataIndex: ['patient', 'lastName'],
    render(text, preAuthorization) {
      return (
        <Link to={toPath.patients.patient({ id: preAuthorization.patient.id })}>
          {preAuthorization.patient.lastName}
          {', '}
          {preAuthorization.patient.firstName}
        </Link>
      )
    },
  },
  {
    title: 'MRN',
    key: 'MRN',
    dataIndex: ['patient', 'practiceMrn'],
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: ['patient', 'birthDate'],
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Billing Date',
    dataIndex: 'billingDate',
    key: 'billingDate',
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, preAuthorization) {
      const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
        preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>{insuranceAuthorizationNumber}</span>
        </DownloadLink>
      )
    },
  },
]

export default function OfficeAuthorizations({
  checkDate,
  onPaginationChange,
  page = 1,
  perPage = 50,
  filters = {},
  rowSelection = null,
}) {
  const { practiceId } = useParams()
  const isAdmin = useIsAdmin()
  const officeAuthorizations = useOfficeAuthorizations(checkDate)
  const { total } = useOfficeAuthorizationsPagination(checkDate)
  const { pending, error, clearError, execute } = useAsync(
    useFetchOfficeAuthorizations(practiceId, page, filters, perPage),
    false,
  )
  const dispatch = useDispatch()
  const { brandWarning } = useTheme()
  useEffect(() => {
    execute(practiceId, page, filters, perPage)
  }, [execute, practiceId, page, filters, perPage])

  const handleAuthorizedChange = useCallback(
    async (id, authorized) => {
      try {
        await dispatch(updateOfficeAuthorization(id, { authorized }))
        message.success('Remote authorization successfully updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.authorized',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const filesColumns = [
      {
        title: 'PDF',
        dataIndex: 'hasReportFile',
        key: 'hasReportFile',
        align: 'center',
        render(text, { hasReportFile }) {
          return hasReportFile === null ? (
            <span>-</span>
          ) : (
            <CircleCheckIcon
              checked={hasReportFile}
              falseStyle={{ color: brandWarning }}
            />
          )
        },
      },
      {
        title: 'Full',
        dataIndex: 'hasReportRawFile',
        key: 'hasReportRawFile',
        align: 'center',
        render(text, { hasReportRawFile }) {
          return hasReportRawFile === null ? (
            <span>-</span>
          ) : (
            <CircleCheckIcon
              checked={hasReportRawFile}
              falseStyle={{ color: brandWarning }}
            />
          )
        },
      },
    ]

    return [
      ...BASE_COLUMNS,
      {
        title: 'Authorized',
        key: 'authorized',
        render: (text, record) => (
          <Switch
            checked={record.authorized}
            checkedChildren="Authorized"
            unCheckedChildren="Not&nbsp;Authorized"
            onChange={authorized =>
              handleAuthorizedChange(record.id, authorized)
            }
          />
        ),
      },
      ...(isAdmin ? filesColumns : []),
      {
        title: 'Actions',
        key: 'actions',
        render(text, preAuthorization) {
          return (
            <Link
              to={`${toPath.billing.officeAuthorizations.officeAuthorization({
                id: preAuthorization.id,
              })}?practiceId=${practiceId}`}
            >
              <Button type="link" icon={<FileSearchOutlined />} />
            </Link>
          )
        },
      },
    ]
  }, [brandWarning, handleAuthorizedChange, isAdmin, practiceId])

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: `/practices/${practiceId}/in-office-authorizations.xlsx`,
          query: {
            check_date: checkDate,
            q: filters?.q,
            medical_provider_id: filters?.medicalProviderId,
            office_location_id: filters?.officeLocationId,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [practiceId, checkDate, filters],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  return (
    <>
      {error && <ListError description={error.message} onClose={clearError} />}
      <Table
        size="middle"
        columns={columns}
        dataSource={officeAuthorizations}
        rowKey="id"
        loading={pending}
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize: Number(perPage),
          onChange: onPaginationChange,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
      {Boolean(officeAuthorizations.length) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </>
  )
}

OfficeAuthorizations.propTypes = {
  checkDate: PropTypes.string.isRequired,
  onPaginationChange: PropTypes.func.isRequired,
  rowSelection: PropTypes.shape({}),
  page: PropTypes.number,
  perPage: PropTypes.number,
  filters: PropTypes.shape({ q: PropTypes.string }),
}
